import React, { useMemo, useState } from 'react';
import { styled } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Colors } from 'library';

interface PasswordInputProps {
    value: string;
    onChange: (value: string) => void;
    id?: string;
    placeholder?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    showRequirements?: boolean;
}

export function PasswordInput({
    value,
    onChange,
    id,
    placeholder,
    disabled,
    fullWidth,
    showRequirements,
}: PasswordInputProps) {
    const [visible, setVisible] = useState(false);
    const [focused, setFocused] = useState(false);

    const requirements = useMemo(
        () => [
            { key: 'length', label: 'Minimum 8 characters', satisfied: value.length >= 8 },
            { key: 'uppercase', label: 'At least one uppercase letter', satisfied: /[A-Z]/.test(value) },
            { key: 'lowercase', label: 'At least one lowercase letter', satisfied: /[a-z]/.test(value) },
            { key: 'digit', label: 'At least one digit', satisfied: /\d/.test(value) },
            {
                key: 'special',
                label: 'At least one special character (#?!@$%^&*-)',
                satisfied: /[#?!@$%^&*-]/.test(value),
            },
        ],
        [value],
    );

    const field = (
        <Wrapper fullWidth={fullWidth}>
            <StyledInput
                id={id}
                type={visible ? 'text' : 'password'}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder={placeholder}
                disabled={disabled}
            />
            <ToggleButton
                type="button"
                onClick={() => setVisible(!visible)}
                tabIndex={-1}
                aria-label={visible ? 'Hide password' : 'Show password'}
            >
                {visible ? (
                    <Visibility style={{ fontSize: 18, color: Colors.mediumGrey }} />
                ) : (
                    <VisibilityOff style={{ fontSize: 18, color: Colors.mediumGrey }} />
                )}
            </ToggleButton>
        </Wrapper>
    );

    if (!showRequirements) return field;

    return (
        <Column fullWidth={fullWidth}>
            {field}
            {(focused || !!value) && (
                <Requirements>
                    {requirements.map((req) => (
                        <Requirement key={req.key} satisfied={req.satisfied}>
                            {req.satisfied ? (
                                <CheckCircleIcon style={{ fontSize: 16, color: Colors.secondary }} />
                            ) : (
                                <CancelIcon style={{ fontSize: 16, color: Colors.error }} />
                            )}
                            {req.label}
                        </Requirement>
                    ))}
                </Requirements>
            )}
        </Column>
    );
}

const Column = styled('div')<{ fullWidth?: boolean }>(({ fullWidth }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: fullWidth ? '100%' : 'auto',
}));

const Requirements = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
});

const Requirement = styled('span')<{ satisfied: boolean }>(({ satisfied }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: satisfied ? Colors.secondary : Colors.error,
}));

const Wrapper = styled('div')<{ fullWidth?: boolean }>(({ fullWidth }) => ({
    position: 'relative',
    display: 'inline-flex',
    width: fullWidth ? '100%' : 'auto',
}));

const StyledInput = styled('input')({
    width: '100%',
    padding: '10px 40px 10px 14px',
    fontSize: 14,
    color: Colors.darkNavy,
    backgroundColor: Colors.faintGrey,
    border: `1px solid ${Colors.lightGrey}`,
    borderRadius: 10,
    outline: 'none',
    boxSizing: 'border-box',
    '&:focus': {
        borderColor: Colors.mediumGrey,
    },
    '&:disabled': {
        backgroundColor: Colors.fill,
        color: Colors.mediumGrey,
    },
    '&::placeholder': {
        color: Colors.lightGrey,
    },
});

const ToggleButton = styled('button')({
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 2,
    display: 'flex',
    alignItems: 'center',
});
